import { Component, OnInit, OnDestroy, ViewChild, TemplateRef, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from '../../../services/toast-service';
import {
  SystemIntegrationMonitorService,
  IntegrationMonitorDto,
  TenantIntegrationStatus,
  IntegrationStatusItem,
  IntegrationExecutionHistoryItem
} from '../../../services/system-integration-monitor.service';
import { ApiErrorLogService, ApiErrorLogListItem } from '../../../services/api-error-log.service';
import { EPortalApiLogService } from '../../../services/eportal-api-log.service';

type FilterMode = 'all' | 'errors' | 'warnings' | 'neverrun' | 'muted' | 'connection';

interface FlattenedError {
  domainId: number;
  tenantName: string;
  integrationId: number | null;
  integrationName: string;
  typeName: string;
  errorMessage: string;
  timeSince: string;
  lastExecutedDate: string;
  isConnection: boolean;
  alertLevel?: string;
}

const LAST_SEEN_KEY = 'integrationMonitor:lastSeen';
const FILTER_PANEL_KEY = 'integrationMonitor:filterPanelOpen';
const SHOW_ACKED_KEY = 'integrationMonitor:showAcked';
const GROUP_BY_KEY = 'integrationMonitor:groupBy';

@Component({
  selector: 'app-integration-monitor',
  templateUrl: './integration-monitor.component.html',
  styleUrls: ['./integration-monitor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IntegrationMonitorComponent implements OnInit, OnDestroy {
  // ── Data ──
  monitorData: IntegrationMonitorDto | null = null;
  loading = false;
  lastRefreshed: Date | null = null;
  errorMessage: string | null = null;

  // ── Filters / search ──
  searchText = '';
  filterMode: FilterMode = 'errors';
  filterType = '';
  filterSeverity = '';
  showAcked = false;
  showFilterPanel = false;
  groupBy: 'customer' | 'type' = 'customer';

  // ── State ──
  expandedTenants: Set<number> = new Set();
  showAllTenants: Set<number> = new Set();
  okDisplayLimit = 24;
  lastSeenAt: Date | null = null;
  newErrorIds = new Set<string>();
  countdownSeconds = 0;

  // ── API logs ──
  recentApiLogs: ApiErrorLogListItem[] = [];
  loadingApiLogs = false;
  recentEPortalLogs: ApiErrorLogListItem[] = [];
  loadingEPortalLogs = false;

  // ── TV mode ──
  tvMode = false;
  autoRefreshInterval = 60;
  isTokenAuthenticated = false;
  private refreshTimer: any = null;
  private countdownTimer: any = null;
  private tvToken: string | null = null;

  // ── Modal state ──
  @ViewChild('historyModal', { static: true }) historyModalTpl!: TemplateRef<any>;
  @ViewChild('muteModal', { static: true }) muteModalTpl!: TemplateRef<any>;

  private historyModalRef?: NgbModalRef;
  private muteModalRef?: NgbModalRef;

  historyLoading = false;
  historyError: string | null = null;
  historyItems: IntegrationExecutionHistoryItem[] = [];
  historyContext: { tenant: TenantIntegrationStatus; integration: IntegrationStatusItem } | null = null;

  muteContext: { tenant: TenantIntegrationStatus; integration: IntegrationStatusItem } | null = null;
  muteHours = 24;
  muteReason = '';
  muteSubmitting = false;

  private boundFocusHandler = (e: KeyboardEvent) => this.handleGlobalShortcut(e);

  constructor(
    private monitorService: SystemIntegrationMonitorService,
    private apiErrorLogService: ApiErrorLogService,
    private ePortalApiLogService: EPortalApiLogService,
    private translateService: TranslateService,
    private toastService: ToastService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    // Wrap mot localStorage-feil i private-mode / kvote-overskridelse — krasjer ellers
    // kontruktøren og hele integration-monitor-siden blir ubrukelig.
    try {
      this.showFilterPanel = localStorage.getItem(FILTER_PANEL_KEY) === '1';
      this.showAcked = localStorage.getItem(SHOW_ACKED_KEY) === '1';
      const stored = localStorage.getItem(GROUP_BY_KEY);
      if (stored === 'customer' || stored === 'type') this.groupBy = stored;

      const seen = localStorage.getItem(LAST_SEEN_KEY);
      if (seen) {
        const t = parseInt(seen, 10);
        if (!isNaN(t)) this.lastSeenAt = new Date(t);
      }
    } catch { /* storage utilgjengelig — bruk in-memory defaults */ }
  }

  ngOnInit(): void {
    const isTvRoute = this.router.url.startsWith('/tv/');

    this.route.queryParams.subscribe(params => {
      this.tvMode = isTvRoute || params['tv'] === '1' || params['tv'] === 'true';
      this.tvToken = params['token'] || null;
      this.isTokenAuthenticated = this.tvMode && !!this.tvToken;
      if (params['interval']) {
        const parsed = parseInt(params['interval'], 10);
        if (parsed >= 10 && parsed <= 600) this.autoRefreshInterval = parsed;
      }
      if (this.tvMode) {
        if (isTvRoute && !this.tvToken) {
          this.errorMessage = this.translateService.instant('IntegrationMonitor.tokenRequired');
          this.loading = false;
          this.cdr.markForCheck();
          return;
        }
        this.filterMode = 'errors';
        this.startAutoRefresh();
        document.body.classList.add('tv-fullscreen');
      } else {
        this.stopAutoRefresh();
        document.body.classList.remove('tv-fullscreen');
      }

      this.loadData();
      if (!this.isTokenAuthenticated) {
        this.loadApiLogs();
        this.loadEPortalLogs();
      }
    });

    if (!this.tvMode) {
      document.addEventListener('keydown', this.boundFocusHandler);
    }
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
    document.body.classList.remove('tv-fullscreen');
    document.removeEventListener('keydown', this.boundFocusHandler);
  }

  // ── Auto refresh + countdown ──

  private startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.countdownSeconds = this.autoRefreshInterval;
    this.refreshTimer = setInterval(() => this.loadData(), this.autoRefreshInterval * 1000);
    this.countdownTimer = setInterval(() => {
      this.countdownSeconds = Math.max(0, this.countdownSeconds - 1);
      if (this.countdownSeconds === 0) this.countdownSeconds = this.autoRefreshInterval;
      this.cdr.markForCheck();
    }, 1000);
  }

  private stopAutoRefresh(): void {
    if (this.refreshTimer) { clearInterval(this.refreshTimer); this.refreshTimer = null; }
    if (this.countdownTimer) { clearInterval(this.countdownTimer); this.countdownTimer = null; }
  }

  // ── Keyboard shortcuts ──

  private handleGlobalShortcut(e: KeyboardEvent): void {
    const target = e.target as HTMLElement;
    const isInputFocus = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable;

    // Ctrl/Cmd + K → focus søk
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      this.focusSearch();
      return;
    }
    // / → focus søk (when not typing in input)
    if (e.key === '/' && !isInputFocus) {
      e.preventDefault();
      this.focusSearch();
      return;
    }
    // Esc → clear søk if focused on søk
    if (e.key === 'Escape' && isInputFocus && (target as HTMLInputElement).id === 'imSearchInput') {
      this.searchText = '';
      (target as HTMLInputElement).blur();
      this.cdr.markForCheck();
    }
  }

  focusSearch(): void {
    const el = document.getElementById('imSearchInput') as HTMLInputElement | null;
    if (el) {
      el.focus();
      el.select();
    }
  }

  // ── Data loading ──

  loadData(): void {
    this.loading = true;
    this.errorMessage = null;

    const data$ = this.tvToken
      ? this.monitorService.getMonitorDataByToken(this.tvToken)
      : this.monitorService.getMonitorData();

    data$.subscribe({
      next: (data) => {
        this.computeNewErrors(data);
        this.monitorData = data;
        this.lastRefreshed = new Date();
        this.loading = false;
        if (data?.tenants && this.expandedTenants.size === 0) {
          for (const tenant of data.tenants) {
            if (tenant.hasErrors && !tenant.connectionError) {
              this.expandedTenants.add(tenant.domainID);
            }
          }
        }
        if (this.tvMode) this.countdownSeconds = this.autoRefreshInterval;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.loading = false;
        const msg = err?.message || this.translateService.instant('IntegrationMonitor.loadError');
        this.errorMessage = msg;
        if (!this.tvMode) {
          this.toastService.show({ type: 'error', data: [msg] });
        }
        this.cdr.markForCheck();
      }
    });
  }

  /** Compute set of error keys that are new since last seen, before applying new data. */
  private computeNewErrors(newData: IntegrationMonitorDto): void {
    if (!this.lastSeenAt || !newData?.tenants) {
      this.newErrorIds.clear();
      return;
    }
    const seen = this.lastSeenAt.getTime();
    const next = new Set<string>();
    for (const t of newData.tenants) {
      for (const i of (t.integrations || [])) {
        if ((i.healthStatus === 'Failed' || i.healthStatus === 'Warning') && !i.isAcked) {
          if (i.lastExecutedDate && new Date(i.lastExecutedDate).getTime() > seen) {
            next.add(`${t.domainID}:${i.integrationID}`);
          }
        }
      }
      if (t.connectionError) {
        next.add(`${t.domainID}:conn`);
      }
    }
    this.newErrorIds = next;
  }

  markAsSeen(): void {
    const now = new Date();
    this.lastSeenAt = now;
    try { localStorage.setItem(LAST_SEEN_KEY, String(now.getTime())); } catch { /* storage utilgjengelig */ }
    this.newErrorIds.clear();
    this.cdr.markForCheck();
  }

  // ── Computed views ──

  get summary() {
    return this.monitorData?.summary;
  }

  /** All active integration types in the current data — for filter dropdown */
  get availableTypes(): string[] {
    if (!this.monitorData?.tenants) return [];
    const set = new Set<string>();
    for (const t of this.monitorData.tenants) {
      for (const i of (t.integrations || [])) {
        if (i.integrationTypeName) set.add(i.integrationTypeName);
      }
    }
    return Array.from(set).sort();
  }

  /** Tenants matching all filters + search */
  get filteredTenants(): TenantIntegrationStatus[] {
    if (!this.monitorData?.tenants) return [];
    const search = this.searchText.trim().toLowerCase();

    let tenants = this.monitorData.tenants.map(t => {
      // Filter integrations within tenant
      let integrations = t.integrations || [];
      if (this.filterType) {
        integrations = integrations.filter(i => i.integrationTypeName === this.filterType);
      }
      if (this.filterSeverity) {
        integrations = integrations.filter(i => (i.alertLevel || '') === this.filterSeverity);
      }
      if (!this.showAcked) {
        integrations = integrations.filter(i => !i.isAcked);
      }
      if (search) {
        const tn = (t.domainName || t.databaseName || '').toLowerCase();
        const tenantMatches = tn.includes(search);
        if (!tenantMatches) {
          integrations = integrations.filter(i =>
            i.integrationName.toLowerCase().includes(search) ||
            (i.integrationTypeName || '').toLowerCase().includes(search)
          );
        }
      }
      return { ...t, integrations };
    });

    // Filter mode (KPI bar selection)
    switch (this.filterMode) {
      case 'errors':
        tenants = tenants.filter(t =>
          (this.hasUnackedFailures(t) || !!t.connectionError));
        break;
      case 'warnings':
        tenants = tenants.filter(t => t.integrations?.some(i => i.healthStatus === 'Warning' && !i.isAcked));
        break;
      case 'neverrun':
        tenants = tenants.filter(t => t.integrations?.some(i => i.healthStatus === 'NeverRun'));
        break;
      case 'muted':
        tenants = tenants.filter(t => t.integrations?.some(i => i.isAcked));
        break;
      case 'connection':
        tenants = tenants.filter(t => !!t.connectionError);
        break;
      case 'all':
      default:
        // no extra filter
        break;
    }

    // Hide tenants with no remaining integrations after filter — unless they have a connection error
    tenants = tenants.filter(t => (t.integrations && t.integrations.length > 0) || !!t.connectionError);

    return tenants.sort((a, b) => {
      const aErr = (this.hasUnackedFailures(a) || !!a.connectionError) ? 1 : 0;
      const bErr = (this.hasUnackedFailures(b) || !!b.connectionError) ? 1 : 0;
      if (aErr !== bErr) return bErr - aErr;
      if (aErr && bErr) return (b.failedLast24h || 0) - (a.failedLast24h || 0);
      const an = (a.domainName || a.databaseName || '').toLowerCase();
      const bn = (b.domainName || b.databaseName || '').toLowerCase();
      return an.localeCompare(bn, 'nb');
    });
  }

  /** Subset that needs action (errors, warnings, connection problems) */
  get errorTenants(): TenantIntegrationStatus[] {
    return this.filteredTenants.filter(t =>
      this.hasUnackedFailures(t) || !!t.connectionError || this.hasWarningsOnly(t));
  }

  get okTenants(): TenantIntegrationStatus[] {
    return this.filteredTenants.filter(t =>
      !this.hasUnackedFailures(t) && !t.connectionError && !this.hasWarningsOnly(t));
  }

  get displayedOkTenants(): TenantIntegrationStatus[] {
    return this.okTenants.slice(0, this.okDisplayLimit);
  }

  showMoreOkTenants(): void {
    this.okDisplayLimit += 24;
  }

  /** Group filtered integrations by type — alternative grouping mode */
  get tenantsByType(): { type: string; integrations: { tenant: TenantIntegrationStatus; item: IntegrationStatusItem }[] }[] {
    const groups = new Map<string, { tenant: TenantIntegrationStatus; item: IntegrationStatusItem }[]>();
    for (const t of this.filteredTenants) {
      for (const i of (t.integrations || [])) {
        const key = i.integrationTypeName || 'Ukjent';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push({ tenant: t, item: i });
      }
    }
    return Array.from(groups.entries())
      .map(([type, integrations]) => ({ type, integrations }))
      .sort((a, b) => a.type.localeCompare(b.type, 'nb'));
  }

  // ── Filter helpers ──

  setFilter(mode: FilterMode): void {
    this.filterMode = this.filterMode === mode ? 'all' : mode;
    this.okDisplayLimit = 24;
    this.cdr.markForCheck();
  }

  toggleFilterPanel(): void {
    this.showFilterPanel = !this.showFilterPanel;
    try { localStorage.setItem(FILTER_PANEL_KEY, this.showFilterPanel ? '1' : '0'); } catch { /* storage utilgjengelig */ }
    this.cdr.markForCheck();
  }

  setShowAcked(value: boolean): void {
    this.showAcked = value;
    try { localStorage.setItem(SHOW_ACKED_KEY, value ? '1' : '0'); } catch { /* storage utilgjengelig */ }
    this.cdr.markForCheck();
  }

  setGroupBy(mode: 'customer' | 'type'): void {
    this.groupBy = mode;
    try { localStorage.setItem(GROUP_BY_KEY, mode); } catch { /* storage utilgjengelig */ }
    this.cdr.markForCheck();
  }

  clearFilters(): void {
    this.filterMode = 'all';
    this.filterType = '';
    this.filterSeverity = '';
    this.searchText = '';
    this.cdr.markForCheck();
  }

  get hasActiveFilters(): boolean {
    return !!this.searchText || !!this.filterType || !!this.filterSeverity ||
      this.filterMode !== 'all' && this.filterMode !== 'errors';
  }

  get activeFilterCount(): number {
    let n = 0;
    if (this.filterType) n++;
    if (this.filterSeverity) n++;
    if (this.showAcked) n++;
    return n;
  }

  // ── Status helpers ──

  hasUnackedFailures(tenant: TenantIntegrationStatus): boolean {
    return (tenant.integrations || []).some(i => i.healthStatus === 'Failed' && !i.isAcked);
  }

  hasWarningsOnly(tenant: TenantIntegrationStatus): boolean {
    if (tenant.connectionError) return false;
    const items = tenant.integrations || [];
    const hasWarning = items.some(i => i.healthStatus === 'Warning' && !i.isAcked);
    const hasFailed = items.some(i => i.healthStatus === 'Failed' && !i.isAcked);
    return hasWarning && !hasFailed;
  }

  toggleTenant(domainId: number): void {
    if (this.expandedTenants.has(domainId)) this.expandedTenants.delete(domainId);
    else this.expandedTenants.add(domainId);
    this.cdr.markForCheck();
  }

  isTenantExpanded(domainId: number): boolean { return this.expandedTenants.has(domainId); }

  toggleShowAll(domainId: number): void {
    if (this.showAllTenants.has(domainId)) this.showAllTenants.delete(domainId);
    else this.showAllTenants.add(domainId);
    this.cdr.markForCheck();
  }

  isShowingAll(domainId: number): boolean { return this.showAllTenants.has(domainId); }

  isErrorNew(domainId: number, integrationId: number | null, isConnection: boolean = false): boolean {
    const key = isConnection ? `${domainId}:conn` : `${domainId}:${integrationId}`;
    return this.newErrorIds.has(key);
  }

  // ── Status hero helpers ──

  /** Tag for hero banner: critical / warning / ok — robust mot manglende felter */
  get heroState(): 'critical' | 'warning' | 'ok' {
    const s = this.summary;
    if (!s) return 'ok';
    // Sjekk også tenantsWithErrors fra de faktiske kundekortene (i tilfelle backend
    // ennå ikke er restartet med nytt HasErrors-grunnlag)
    const hasUnacked = (this.monitorData?.tenants || []).some(t =>
      this.hasUnackedFailures(t) || !!t.connectionError);
    if ((s.criticalCount ?? 0) > 0 || (s.tenantsWithErrors ?? 0) > 0 ||
        (s.failedLast24h ?? 0) > 0 || hasUnacked) {
      return 'critical';
    }
    const hasWarn = (this.monitorData?.tenants || []).some(t => this.hasWarningsOnly(t));
    if ((s.warningCount ?? 0) > 0 || (s.neverExecuted ?? 0) > 0 || hasWarn) return 'warning';
    return 'ok';
  }

  /** Effektiv kundetelling med feil — uavhengig av backend-feltet */
  get effectiveErrorTenantCount(): number {
    if (!this.monitorData?.tenants) return 0;
    return this.monitorData.tenants.filter(t => this.hasUnackedFailures(t) || !!t.connectionError).length;
  }

  get effectiveWarningCount(): number {
    if (!this.monitorData?.tenants) return 0;
    return this.monitorData.tenants.reduce((sum, t) =>
      sum + (t.integrations || []).filter(i => i.healthStatus === 'Warning' && !i.isAcked).length, 0);
  }

  get effectiveMutedCount(): number {
    if (this.summary?.mutedCount !== undefined) return this.summary.mutedCount;
    if (!this.monitorData?.tenants) return 0;
    return this.monitorData.tenants.reduce((sum, t) =>
      sum + (t.integrations || []).filter(i => i.isAcked).length, 0);
  }

  get failedTrend(): 'up' | 'down' | 'flat' {
    const s = this.summary;
    if (!s || s.failedPrevious24h === undefined) return 'flat';
    if (s.failedLast24h > s.failedPrevious24h) return 'up';
    if (s.failedLast24h < s.failedPrevious24h) return 'down';
    return 'flat';
  }

  get failedTrendDelta(): number {
    const s = this.summary;
    if (!s || s.failedPrevious24h === undefined) return 0;
    return s.failedLast24h - s.failedPrevious24h;
  }

  /** Has the backend been updated with the new summary fields? */
  get hasNewSummary(): boolean {
    return this.summary?.warningCount !== undefined && this.summary?.criticalCount !== undefined;
  }

  get newErrorCount(): number { return this.newErrorIds.size; }

  get connectionErrorCount(): number {
    if (!this.monitorData?.tenants) return 0;
    return this.monitorData.tenants.filter(t => !!t.connectionError).length;
  }

  /** Time since last seen, e.g. "12m" */
  get lastSeenAgo(): string {
    if (!this.lastSeenAt) return '';
    const ms = Date.now() - this.lastSeenAt.getTime();
    return this.humanizeMs(ms);
  }

  // ── Health & badge ──

  getHealthBadgeClass(status: string): string {
    switch (status) {
      case 'OK': return 'badge-soft-success';
      case 'Failed': return 'badge-soft-danger';
      case 'Warning': return 'badge-soft-warning';
      case 'Running': return 'badge-soft-info';
      case 'NeverRun': return 'badge-soft-dark';
      case 'Inactive': return 'bg-light text-muted border';
      default: return 'badge-soft-dark';
    }
  }

  getHealthLabel(status: string): string {
    return this.translateService.instant(`IntegrationMonitor.health.${status}`);
  }

  getSeverityClass(level?: string): string {
    if (!level) return 'badge-soft-secondary';
    const l = level.toLowerCase();
    if (l === 'p1' || l === 'critical') return 'badge-soft-danger';
    if (l === 'p2' || l === 'high') return 'badge-soft-warning';
    if (l === 'p3' || l === 'low') return 'badge-soft-info';
    return 'badge-soft-secondary';
  }

  // ── Formatting ──

  formatDuration(ms: number | undefined): string {
    if (ms == null) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  }

  formatDate(dateStr: string | undefined): string {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  getTimeSince(dateStr: string | undefined): string {
    if (!dateStr) return '';
    const ms = Date.now() - new Date(dateStr).getTime();
    return this.humanizeMs(ms);
  }

  private humanizeMs(ms: number): string {
    if (ms < 0) ms = 0;
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return '<1m';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}t`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  }

  truncateError(msg: string | undefined): string {
    if (!msg) return '';
    const cleaned = msg.replace(/\r?\n/g, ' ').trim();
    return cleaned.length <= 30 ? cleaned : cleaned.substring(0, 28) + '…';
  }

  truncateApiLogMessage(msg?: string): string {
    if (!msg) return '';
    const cleaned = msg.replace(/\r?\n/g, ' ').trim();
    return cleaned.length <= 80 ? cleaned : cleaned.substring(0, 78) + '…';
  }

  getChipTooltip(item: IntegrationStatusItem): string {
    const parts: string[] = [item.integrationName + ' — ' + this.getHealthLabel(item.healthStatus)];
    if (item.integrationTypeName) parts.push('Type: ' + item.integrationTypeName);
    if (item.lastExecutedDate) parts.push('Siste kjøring: ' + this.getTimeSince(item.lastExecutedDate) + ' siden');
    if (item.lastSuccessfulRunDate) parts.push('Sist OK: ' + this.getTimeSince(item.lastSuccessfulRunDate) + ' siden');
    if (item.lastRecordsProcessed != null) parts.push('Poster: ' + (item.lastRecordsSucceeded ?? 0) + '/' + item.lastRecordsProcessed + (item.lastRecordsFailed ? ' (' + item.lastRecordsFailed + ' feilet)' : ''));
    if (item.lastExecutionDurationMs != null) parts.push('Varighet: ' + this.formatDuration(item.lastExecutionDurationMs));
    if (item.isAcked) parts.push('Dempet av ' + (item.ackedBy || '?') + ' til ' + this.formatDate(item.ackedUntil));
    if (item.lastErrorMessage) parts.push('Feil: ' + item.lastErrorMessage);
    return parts.join('\n');
  }

  // ── Per-tenant helpers ──

  getFailedIntegrations(tenant: TenantIntegrationStatus): IntegrationStatusItem[] {
    return (tenant.integrations || []).filter(i =>
      (i.healthStatus === 'Failed' || i.healthStatus === 'Warning') && !i.isAcked);
  }

  getOkIntegrations(tenant: TenantIntegrationStatus): IntegrationStatusItem[] {
    return (tenant.integrations || []).filter(i => i.healthStatus === 'OK');
  }

  getMutedIntegrations(tenant: TenantIntegrationStatus): IntegrationStatusItem[] {
    return (tenant.integrations || []).filter(i => i.isAcked);
  }

  getTenantTotalRecords(tenant: TenantIntegrationStatus): number {
    return (tenant.integrations || []).reduce((sum, i) => sum + (i.lastRecordsProcessed ?? 0), 0);
  }

  getLastOkRunTime(tenant: TenantIntegrationStatus): string {
    const okItems = this.getOkIntegrations(tenant);
    if (okItems.length === 0) return '-';
    let latest = '';
    for (const item of okItems) {
      if (item.lastExecutedDate && item.lastExecutedDate > latest) latest = item.lastExecutedDate;
    }
    return latest ? this.getTimeSince(latest) + ' siden' : '-';
  }

  getFailedIntegrationNames(tenant: TenantIntegrationStatus): string {
    const failed = this.getFailedIntegrations(tenant);
    const names = failed.map(i => i.integrationName);
    if (names.length <= 3) return names.join(', ');
    return names.slice(0, 3).join(', ') + ` (+${names.length - 3})`;
  }

  // ── Live error feed ──

  getRecentErrors(): FlattenedError[] {
    if (!this.monitorData?.tenants) return [];
    const errors: FlattenedError[] = [];
    for (const tenant of this.monitorData.tenants) {
      if (tenant.connectionError) {
        errors.push({
          domainId: tenant.domainID,
          tenantName: tenant.domainName || tenant.databaseName,
          integrationId: null,
          integrationName: 'Tilkobling',
          typeName: 'Database',
          errorMessage: tenant.connectionError,
          timeSince: '',
          lastExecutedDate: '',
          isConnection: true
        });
        continue;
      }
      for (const item of (tenant.integrations || [])) {
        if ((item.healthStatus === 'Failed' || item.healthStatus === 'Warning') && !item.isAcked) {
          errors.push({
            domainId: tenant.domainID,
            tenantName: tenant.domainName || tenant.databaseName,
            integrationId: item.integrationID,
            integrationName: item.integrationName,
            typeName: item.integrationTypeName,
            errorMessage: item.lastErrorMessage || '',
            timeSince: this.getTimeSince(item.lastExecutedDate),
            lastExecutedDate: item.lastExecutedDate || '',
            isConnection: false,
            alertLevel: item.alertLevel
          });
        }
      }
    }
    return errors.sort((a, b) => {
      if (!a.lastExecutedDate) return 1;
      if (!b.lastExecutedDate) return -1;
      return b.lastExecutedDate.localeCompare(a.lastExecutedDate);
    });
  }

  // ── Deep links ──

  /** Open the api-error-log filtered by tenant + integration */
  openInLogs(tenantName: string | undefined, integrationName?: string): void {
    const params: any = {};
    if (tenantName) params.domain = tenantName;
    if (integrationName) params.search = integrationName;
    this.router.navigate(['/system-admin/api-error-log'], { queryParams: params });
  }

  openInEPortalLogs(tenantName: string | undefined, integrationName?: string): void {
    const params: any = {};
    if (tenantName) params.domain = tenantName;
    if (integrationName) params.search = integrationName;
    this.router.navigate(['/system-admin/eportal-api-log'], { queryParams: params });
  }

  openTenantSite(tenant: TenantIntegrationStatus): void {
    if (!tenant.domainName) return;
    const url = `https://${tenant.domainName}/`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  // ── History modal ──

  openHistoryModal(tenant: TenantIntegrationStatus, item: IntegrationStatusItem): void {
    this.historyContext = { tenant, integration: item };
    this.historyItems = [];
    this.historyError = null;
    this.historyLoading = true;
    this.historyModalRef = this.modalService.open(this.historyModalTpl, { size: 'lg', scrollable: true });
    this.monitorService.getHistory(tenant.domainID, item.integrationID, 10).subscribe({
      next: (response) => {
        this.historyLoading = false;
        if (response?.success) {
          this.historyItems = response.result || [];
        } else {
          this.historyError = this.translateService.instant('IntegrationMonitor.historyLoadError');
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.historyLoading = false;
        this.historyError = this.translateService.instant('IntegrationMonitor.historyLoadError');
        this.cdr.markForCheck();
      }
    });
  }

  closeHistoryModal(): void { this.historyModalRef?.close(); this.historyContext = null; }

  // ── Mute modal ──

  openMuteModal(tenant: TenantIntegrationStatus, item: IntegrationStatusItem, defaultHours: number = 24): void {
    this.muteContext = { tenant, integration: item };
    this.muteHours = defaultHours;
    this.muteReason = '';
    this.muteSubmitting = false;
    this.muteModalRef = this.modalService.open(this.muteModalTpl, { size: 'md' });
  }

  confirmMute(): void {
    if (!this.muteContext) return;
    const { tenant, integration } = this.muteContext;
    this.muteSubmitting = true;
    this.monitorService.ackIntegration(tenant.domainID, integration.integrationID, this.muteHours, this.muteReason || undefined).subscribe({
      next: (resp) => {
        this.muteSubmitting = false;
        if (resp?.success) {
          this.toastService.show({ type: 'success', data: [this.translateService.instant('IntegrationMonitor.muteToastSuccess')] });
          this.muteModalRef?.close();
          this.muteContext = null;
          this.loadData();
        } else {
          this.toastService.show({ type: 'error', data: [this.translateService.instant('IntegrationMonitor.muteToastError')] });
        }
      },
      error: () => {
        this.muteSubmitting = false;
        this.toastService.show({ type: 'error', data: [this.translateService.instant('IntegrationMonitor.muteToastError')] });
      }
    });
  }

  cancelMute(): void { this.muteModalRef?.close(); this.muteContext = null; }

  unmute(tenant: TenantIntegrationStatus, item: IntegrationStatusItem): void {
    this.monitorService.unackIntegration(tenant.domainID, item.integrationID).subscribe({
      next: (resp) => {
        if (resp?.success) {
          this.toastService.show({ type: 'success', data: [this.translateService.instant('IntegrationMonitor.unmuteToastSuccess')] });
          this.loadData();
        } else {
          this.toastService.show({ type: 'error', data: [this.translateService.instant('IntegrationMonitor.unmuteToastError')] });
        }
      },
      error: () => {
        this.toastService.show({ type: 'error', data: [this.translateService.instant('IntegrationMonitor.unmuteToastError')] });
      }
    });
  }

  // ── CSV export ──

  exportCsv(): void {
    if (!this.monitorData?.tenants) return;
    const rows: string[][] = [
      ['Kunde', 'Integrasjon', 'Type', 'Status', 'Sist kjørt', 'Sist OK', 'Poster', 'Varighet (ms)', 'Feilmelding', 'Dempet']
    ];
    for (const t of this.monitorData.tenants) {
      if (t.connectionError) {
        rows.push([t.domainName || t.databaseName, 'Tilkobling', 'Database', 'Failed', '', '', '', '', t.connectionError, '']);
      }
      for (const i of (t.integrations || [])) {
        const records = i.lastRecordsProcessed != null ? `${i.lastRecordsSucceeded ?? 0}/${i.lastRecordsProcessed}` : '';
        rows.push([
          t.domainName || t.databaseName,
          i.integrationName,
          i.integrationTypeName,
          i.healthStatus,
          i.lastExecutedDate ? this.formatDate(i.lastExecutedDate) : '',
          i.lastSuccessfulRunDate ? this.formatDate(i.lastSuccessfulRunDate) : '',
          records,
          i.lastExecutionDurationMs != null ? String(i.lastExecutionDurationMs) : '',
          (i.lastErrorMessage || '').replace(/\r?\n/g, ' '),
          i.isAcked ? `Til ${this.formatDate(i.ackedUntil)}` : ''
        ]);
      }
    }
    const csv = rows.map(r => r.map(cell => {
      const val = cell ?? '';
      return /[",;\n]/.test(val) ? `"${val.replace(/"/g, '""')}"` : val;
    }).join(';')).join('\r\n');

    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const today = new Date().toISOString().substring(0, 10);
    a.download = `${this.translateService.instant('IntegrationMonitor.csvFilenamePrefix')}-${today}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ── API logs ──

  loadApiLogs(): void {
    this.loadingApiLogs = true;
    this.apiErrorLogService.getRecent(15).subscribe({
      next: (response) => {
        if (response?.success) this.recentApiLogs = response.result || [];
        this.loadingApiLogs = false;
        this.cdr.markForCheck();
      },
      error: () => { this.loadingApiLogs = false; this.cdr.markForCheck(); }
    });
  }

  loadEPortalLogs(): void {
    this.loadingEPortalLogs = true;
    this.ePortalApiLogService.getRecent(15).subscribe({
      next: (response) => {
        if (response?.success) this.recentEPortalLogs = response.result || [];
        this.loadingEPortalLogs = false;
        this.cdr.markForCheck();
      },
      error: () => { this.loadingEPortalLogs = false; this.cdr.markForCheck(); }
    });
  }

  getApiLogLevelClass(level?: string): string {
    switch (level) {
      case 'Fatal': return 'badge-soft-dark';
      case 'Error': return 'badge-soft-danger';
      case 'Warning': return 'badge-soft-warning';
      default: return 'badge-soft-secondary';
    }
  }

  formatApiLogTime(timeStamp: string): string {
    if (!timeStamp) return '';
    const d = new Date(timeStamp);
    return d.toLocaleString('nb-NO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  }

  // ── TV countdown helpers ──

  /** Returns 0..1 progress of countdown ring (0 = just refreshed, 1 = about to refresh) */
  get countdownProgress(): number {
    if (!this.autoRefreshInterval) return 0;
    return 1 - (this.countdownSeconds / this.autoRefreshInterval);
  }

  get countdownDashOffset(): number {
    // Circle circumference (r=18 → 2πr ≈ 113)
    const C = 113;
    return C * (this.countdownSeconds / this.autoRefreshInterval);
  }

  // ── trackBy ──
  trackByDomain = (_: number, t: TenantIntegrationStatus) => t.domainID;
  trackByIntegration = (_: number, i: IntegrationStatusItem) => i.integrationID;
  trackByType = (_: number, g: { type: string }) => g.type;
  trackByErrorKey = (_: number, e: FlattenedError) => `${e.domainId}:${e.integrationId ?? 'conn'}:${e.lastExecutedDate}`;
  trackByApiLog = (_: number, l: ApiErrorLogListItem) => l.id;
  trackByExecution = (_: number, h: IntegrationExecutionHistoryItem) => h.executionID;
}
